/**
 * Iter 223 · Health Ops Extras — Green Corridor · Insurance · Fairness
 * (Triage is a customer-facing endpoint; admin sees the logs bucket here.)
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Broadcast, ShieldCheck, Scales, Brain, Plus, X, ArrowClockwise, PlayCircle,
} from "@phosphor-icons/react";

const TABS = [
  { key: "corridor",  label: "Green Corridor",   icon: Broadcast   },
  { key: "insurance", label: "Insurance Bridge", icon: ShieldCheck },
  { key: "fairness",  label: "Fairness Watchdog", icon: Scales    },
  { key: "triage",    label: "AI Triage Logs",   icon: Brain      },
];

export default function HealthOpsExtras() {
  const [tab, setTab] = useState("corridor");
  return (
    <AppShell title="Health Ops · Extras" subtitle="Corridor · Insurance · Fairness · Triage">
      <div className="space-y-4" data-testid="health-extras-root">
        <div className="flex items-center gap-2 border-b border-[#E5E2D9] overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`extras-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
                  active ? "border-[#B91C1C] text-[#B91C1C]" : "border-transparent text-[#5C5A55]"
                }`}>
                <Icon size={14} weight={active?"fill":"regular"}/>
                {t.label}
              </button>
            );
          })}
        </div>
        {tab === "corridor"  && <CorridorTab/>}
        {tab === "insurance" && <InsuranceTab/>}
        {tab === "fairness"  && <FairnessTab/>}
        {tab === "triage"    && <TriageTab/>}
      </div>
    </AppShell>
  );
}

// ---- Corridor ------------------------------------------------------
function CorridorTab() {
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showPlayback, setShowPlayback] = useState(false);
  const load = async () => {
    try {
      const [s, e] = await Promise.all([
        api.get("/health-extras/corridor/subscribers"),
        api.get("/health-extras/corridor/events?days=3"),
      ]);
      setSubs(s.data.subscribers || []);
      setEvents(e.data.events || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Remove subscriber?")) return;
    await api.delete(`/health-extras/corridor/subscribers/${id}`); load();
  };
  return (
    <div className="space-y-4" data-testid="corridor-tab">
      <div className="p-4 rounded-lg bg-[#E0E7FF] border border-[#C7D2FE] text-sm text-[#312E81]">
        <b>Green Corridor</b> auto-notifies subscribed ATC / traffic-police / hospital endpoints
        whenever an ambulance flips into <code>en_route_pickup</code> or <code>en_route_hospital</code>.
        Each event carries mission_no, ambulance reg, urgency, from/to and ETA.
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#5C5A55]">{subs.length} subscribers · {events.length} events (last 3d)</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPlayback(true)} data-testid="corridor-playback"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-[#4338CA] text-[#4338CA]">
            <PlayCircle size={14}/> Live playback
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="corridor-add"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-[#4338CA] text-white">
            <Plus size={14}/> Add subscriber
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-3">Subscribers</div>
          {subs.length === 0 ? <div className="text-sm text-[#8A8780]">No subscribers yet</div>
            : subs.map(s => (
              <div key={s.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-[11px] text-[#8A8780]">{s.kind} · {s.endpoint_url}</div>
                </div>
                <button onClick={() => del(s.id)} className="text-xs text-[#B91C1C]">Remove</button>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-3">Recent broadcast events</div>
          {events.length === 0 ? <div className="text-sm text-[#8A8780]">No events yet</div>
            : events.map(e => (
              <div key={e.id} className="py-2 border-b last:border-0">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono text-[11px]">{e.mission_no}</span>
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#FEE2E2] text-[#B91C1C]">{e.status?.replace(/_/g," ")}</span>
                </div>
                <div className="text-[11px] text-[#8A8780]">{e.ambulance_reg_no} · {new Date(e.ts).toLocaleString()}</div>
              </div>
            ))}
        </div>
      </div>
      {showAdd && <CorridorAddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }}/>}
      {showPlayback && <CorridorPlaybackModal onClose={() => setShowPlayback(false)}/>}
    </div>
  );
}

function CorridorPlaybackModal({ onClose }) {
  const [minutes, setMinutes] = useState(60);
  const [data, setData] = useState(null);
  const [t, setT] = useState(0);  // 0-100 slider position
  const load = async () => {
    try {
      const r = await api.get(`/health-extras/corridor/playback?minutes=${minutes}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [minutes]);

  // Compute the "at time t" set of missions the UI should show
  const winFrom = data?.window?.from ? new Date(data.window.from).getTime() : 0;
  const winTo   = data?.window?.to   ? new Date(data.window.to).getTime()   : 0;
  const cursorMs = winFrom + (winTo - winFrom) * (t / 100);
  const eventsBefore = (data?.events || []).filter(e => new Date(e.ts).getTime() <= cursorMs);
  const missionsAtCursor = (data?.missions || []).map(m => {
    // Last history row <= cursor
    const hist = (m.history || []).filter(h => new Date(h.at).getTime() <= cursorMs);
    const last = hist[hist.length - 1];
    return { ...m, cursor_status: last?.status, cursor_ts: last?.at };
  }).filter(m => m.cursor_status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}
          data-testid="corridor-playback-modal">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg">Corridor Live Playback</h2>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <label className="text-sm text-[#5C5A55]">Window:</label>
          <select value={minutes} onChange={e => setMinutes(Number(e.target.value))} className="border rounded px-2 py-1 text-sm">
            <option value={15}>Last 15 min</option>
            <option value={60}>Last 1 hour</option>
            <option value={180}>Last 3 hours</option>
            <option value={720}>Last 12 hours</option>
          </select>
          <div className="text-sm text-[#8A8780]">
            {data?.events?.length ?? 0} events · {data?.missions?.length ?? 0} missions
          </div>
        </div>
        <div className="mb-3">
          <input
            type="range" min={0} max={100} value={t}
            onChange={e => setT(Number(e.target.value))}
            data-testid="playback-slider"
            className="w-full"
          />
          <div className="flex justify-between text-[10px] text-[#8A8780] mt-1">
            <span>{data?.window ? new Date(data.window.from).toLocaleTimeString() : "—"}</span>
            <span className="font-medium text-[#4338CA]">Cursor: {new Date(cursorMs).toLocaleTimeString()}</span>
            <span>{data?.window ? new Date(data.window.to).toLocaleTimeString() : "—"}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 border border-[#E5E2D9] rounded-lg">
            <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Missions at cursor</div>
            {missionsAtCursor.length === 0 ? (
              <div className="text-sm text-[#8A8780]">No missions in window</div>
            ) : missionsAtCursor.map(m => (
              <div key={m.id} className="py-2 border-b last:border-0" data-testid={`playback-mission-${m.mission_no}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[11px]">{m.mission_no}</div>
                    <div className="text-[11px] text-[#8A8780]">{m.ambulance_reg_no} · {m.amb_type?.toUpperCase()}</div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#FEE2E2] text-[#B91C1C]">{m.cursor_status?.replace(/_/g," ")}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border border-[#E5E2D9] rounded-lg">
            <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Broadcast events up to cursor</div>
            {eventsBefore.length === 0 ? (
              <div className="text-sm text-[#8A8780]">No events yet</div>
            ) : eventsBefore.slice(-10).map(e => (
              <div key={e.id} className="py-1 text-sm border-b last:border-0">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px]">{e.mission_no}</span>
                  <span className="text-[10px] text-[#8A8780]">{new Date(e.ts).toLocaleTimeString()}</span>
                </div>
                <div className="text-[11px] text-[#5C5A55]">{e.status?.replace(/_/g," ")} · {e.ambulance_reg_no}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CorridorAddModal({ onClose, onSaved }) {
  const [f, setF] = useState({kind:"traffic_police", name:"", endpoint_url:"", webhook_secret:""});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name || !f.endpoint_url) { toast.error("Name and endpoint required"); return; }
    try { setBusy(true); await api.post("/health-extras/corridor/subscribers", f); toast.success("Subscriber added"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h2 className="font-heading text-lg">Add subscriber</h2><button onClick={onClose}><X size={16}/></button></div>
        <div className="space-y-3">
          <select value={f.kind} onChange={e => setF({...f, kind:e.target.value})} className="w-full border rounded px-3 py-2">
            <option value="traffic_police">Traffic Police</option><option value="atc">ATC</option><option value="hospital">Hospital</option>
          </select>
          <input placeholder="Name" data-testid="corridor-name" value={f.name} onChange={e => setF({...f, name:e.target.value})} className="w-full border rounded px-3 py-2"/>
          <input placeholder="Webhook URL" data-testid="corridor-url" value={f.endpoint_url} onChange={e => setF({...f, endpoint_url:e.target.value})} className="w-full border rounded px-3 py-2"/>
          <input placeholder="Webhook secret (optional)" value={f.webhook_secret} onChange={e => setF({...f, webhook_secret:e.target.value})} className="w-full border rounded px-3 py-2"/>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded">Cancel</button>
          <button data-testid="corridor-save" disabled={busy} onClick={save} className="px-3 py-1.5 text-sm bg-[#4338CA] text-white rounded disabled:opacity-50">Add</button>
        </div>
      </div>
    </div>
  );
}

// ---- Insurance ----------------------------------------------------
function InsuranceTab() {
  const [preauth, setPreauth] = useState([]);
  const [claims, setClaims] = useState([]);
  const [scorecard, setScorecard] = useState([]);
  const load = async () => {
    try {
      const [pa, cl, sc] = await Promise.all([
        api.get("/insurance/preauth"),
        api.get("/insurance/claims"),
        api.get("/insurance/scorecard"),
      ]);
      setPreauth(pa.data.preauth || []);
      setClaims(cl.data.claims || []);
      setScorecard(sc.data.scorecard || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="insurance-tab">
      <div className="p-4 rounded-lg bg-[#CCFBF1] border border-[#5EEAD4] text-sm text-[#134E4A]">
        <b>Insurance Bridge</b> · Hospital tier raises pre-auth from within a mission → insurer approves within SLA (6h target)
        → claim packet auto-submits at mission close → status flows through <code>submitted → approved → paid</code>. 21-day claim SLA.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniTable title="Pre-auth · latest 5" data-testid="preauth-mini" rows={preauth.slice(0,5)} col1="preauth_no" col2="patient_name" statusKey="status"/>
        <MiniTable title="Claims · latest 5" data-testid="claims-mini" rows={claims.slice(0,5)} col1="claim_no" col2="invoice_no" statusKey="status"/>
        <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-3">Insurer scorecard</div>
          {scorecard.length === 0 ? <div className="text-sm text-[#8A8780]">No claims yet</div>
            : scorecard.map(r => (
              <div key={r._id} className="flex items-center justify-between py-1 text-sm">
                <span className="font-mono text-[12px]">{r._id}</span>
                <span className="text-[11px] text-[#5C5A55]">{r.approved}/{r.total} approved · {r.paid} paid</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function MiniTable({ title, rows, col1, col2, statusKey, ...rest }) {
  const STYLE = {
    submitted:"bg-[#DBEAFE] text-[#1D4ED8]", queried:"bg-[#FEF3C7] text-[#B45309]",
    approved:"bg-[#D1FAE5] text-[#065F46]", partial:"bg-[#FEF3C7] text-[#B45309]",
    declined:"bg-[#FEE2E2] text-[#B91C1C]", rejected:"bg-[#FEE2E2] text-[#B91C1C]",
    paid:"bg-[#D4E0D1] text-[#1A3317]",
  };
  return (
    <div className="bg-white rounded-lg border border-[#E5E2D9] p-4" {...rest}>
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-3">{title}</div>
      {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No records</div>
        : rows.map(r => (
          <div key={r.id} className="flex items-center justify-between py-1 text-sm">
            <div>
              <div className="font-mono text-[11px]">{r[col1]}</div>
              <div className="text-[11px] text-[#8A8780]">{r[col2]}</div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[11px] ${STYLE[r[statusKey]] || ""}`}>{r[statusKey]}</span>
          </div>
        ))}
    </div>
  );
}

// ---- Fairness -----------------------------------------------------
function FairnessTab() {
  const [dash, setDash] = useState(null);
  const [nudges, setNudges] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [d, n] = await Promise.all([
        api.get("/health-extras/fairness/dashboard"),
        api.get("/health-extras/fairness/nudges"),
      ]);
      setDash(d.data);
      setNudges(n.data.nudges || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const runNow = async (cure) => {
    setBusy(true);
    try {
      const url = cure === 24
        ? "/health-extras/fairness/run-now-v2?cure_hours=24"
        : "/health-extras/fairness/run-now?cure_hours=0";
      const r = await api.post(url);
      toast.success(cure === 24
        ? `Watchdog · notices ${r.data.notices_sent} · cured ${r.data.cured}`
        : `Watchdog · paused ${r.data.paused_now || 0} provider(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const unpause = async (pid) => {
    try { await api.post(`/health-extras/fairness/unpause/${pid}`); toast.success("Unpaused"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="fairness-tab">
      <div className="p-4 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] text-sm text-[#7A5A11] flex items-start justify-between gap-4">
        <div>
          <b>Fairness watchdog</b> runs hourly. If a provider's 7-day free-tier wait exceeds
          3× the priority wait, InstaBook is auto-paused for that provider and a fairness
          event is logged. Superadmin can unpause manually.
        </div>
        <button data-testid="fairness-run" onClick={() => runNow(24)} disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-[#B45309] text-white disabled:opacity-50">
          <PlayCircle size={14}/> {busy ? "Running…" : "Run (24h grace)"}
        </button>
      </div>
      {nudges.length > 0 && (
        <div className="bg-white rounded-lg border border-[#E5E2D9] p-4" data-testid="fairness-nudges">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-3">Active nudges · 24h cure window</div>
          {nudges.slice(0, 10).map(n => {
            const STYLE = { notice_sent: "bg-[#FEF3C7] text-[#B45309]",
                             cured: "bg-[#D4E0D1] text-[#1A3317]",
                             paused_after_grace: "bg-[#FEE2E2] text-[#B91C1C]" };
            return (
              <div key={n.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <div className="font-mono text-[11px]">{n.provider_id}</div>
                  <div className="text-[11px] text-[#8A8780]">
                    ratio {n.ratio}× · sent {new Date(n.at).toLocaleString()}
                    {n.status === "notice_sent" && n.grace_expires_at && ` · cure by ${new Date(n.grace_expires_at).toLocaleString()}`}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[11px] ${STYLE[n.status] || ""}`}>{n.status?.replace(/_/g, " ")}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Paused providers" value={dash?.paused_providers ?? "—"} c="#B91C1C" bg="#FEE2E2"/>
        <StatTile label="Tracked providers" value={dash?.snapshot?.length ?? "—"} c="#4338CA" bg="#E0E7FF"/>
        <StatTile label="Recent events" value={dash?.recent_events?.length ?? "—"} c="#B45309" bg="#FEF3C7"/>
        <StatTile label="Last computed" value={dash?.generated_at ? new Date(dash.generated_at).toLocaleTimeString() : "—"} c="#065F46" bg="#D1FAE5"/>
      </div>
      <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
        <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-3">Fairness snapshot (7d)</div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-[#8A8780]">
            <tr><th className="text-left py-1">Provider</th><th className="text-right py-1">Free</th><th className="text-right py-1">Priority</th><th className="text-right py-1">Free wait</th><th className="text-right py-1">Priority wait</th><th className="text-right py-1">Ratio</th><th></th></tr>
          </thead>
          <tbody>
            {(dash?.snapshot || []).length === 0 && <tr><td colSpan={7} className="text-center py-4 text-[#8A8780]">No InstaBook activity in 7d</td></tr>}
            {(dash?.snapshot || []).map(r => (
              <tr key={r.provider_id} data-testid={`fair-row-${r.provider_id}`} className={r.fair ? "" : "bg-[#FEE2E2]"}>
                <td className="py-1 font-mono text-[11px]">{r.provider_id}</td>
                <td className="py-1 text-right">{r.free_bookings}</td>
                <td className="py-1 text-right">{r.priority_bookings}</td>
                <td className="py-1 text-right">{r.avg_free_wait_s}s</td>
                <td className="py-1 text-right">{r.avg_priority_wait_s}s</td>
                <td className="py-1 text-right font-medium" style={{color: r.fair ? "#065F46" : "#B91C1C"}}>{r.ratio_free_over_priority}×</td>
                <td className="py-1 text-right">{!r.fair && <button onClick={() => unpause(r.provider_id)} className="text-xs text-[#4338CA]">Unpause</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatTile({ label, value, c, bg }) {
  return (
    <div className="p-4 bg-white rounded-lg border border-[#E5E2D9]">
      <div className="text-[24px] font-heading font-semibold" style={{color:c}}>{value}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}

// ---- Triage logs --------------------------------------------------
function TriageTab() {
  const [logs, setLogs] = useState([]);
  const [buckets, setBuckets] = useState({});
  useEffect(() => {
    api.get("/health-extras/triage/logs?days=7")
      .then(r => { setLogs(r.data.logs || []); setBuckets(r.data.by_outcome || {}); })
      .catch(() => {});
  }, []);
  const STYLE = {
    self_care:"bg-[#D4E0D1] text-[#1A3317]", consult:"bg-[#DBEAFE] text-[#1D4ED8]",
    lab:"bg-[#FEF3C7] text-[#B45309]", er:"bg-[#FED7AA] text-[#C2410C]",
    ambulance:"bg-[#FEE2E2] text-[#B91C1C]",
  };
  return (
    <div className="space-y-4" data-testid="triage-tab">
      <div className="p-4 rounded-lg bg-[#DBEAFE] border border-[#93C5FD] text-sm text-[#1E3A8A]">
        <b>AI Symptom Triage</b> · Customer app posts to <code>POST /api/me/triage/chat</code>
        with symptoms + age + duration → Gemini classifies into 5 outcomes using a
        conservative clinical rubric. Every call is logged for audit.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["self_care","consult","lab","er","ambulance"].map(k => (
          <div key={k} className="p-4 bg-white rounded-lg border border-[#E5E2D9] text-center">
            <div className="text-2xl font-heading" style={{color: (STYLE[k]||"").match(/#[0-9A-F]+/)?.[0]}}>{buckets[k] || 0}</div>
            <div className="text-[11px] uppercase tracking-wider text-[#8A8780] mt-1">{k.replace(/_/g," ")}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">When</th><th className="text-left px-4 py-3">Symptoms</th><th className="text-center px-4 py-3">Outcome</th></tr>
          </thead>
          <tbody className="divide-y divide-[#F0EDE4]">
            {logs.slice(0,50).map(l => (
              <tr key={l.id} data-testid={`triage-row-${l.id}`}>
                <td className="px-4 py-3 text-[11px] text-[#8A8780]">{new Date(l.at).toLocaleString()}</td>
                <td className="px-4 py-3">{(l.symptoms||"").slice(0, 100)}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-[11px] ${STYLE[l.outcome]||""}`}>{l.outcome}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
